import React from "react";
import { generate } from "shortid";
import { Event, EventType, MediaAspectRatio } from "@markit/common.types";
import { API_BASE } from "../API";
import {
  firebaseStorage,
  getDownloadURL,
  ref,
  uploadBytes,
  listAll,
  deleteObject,
} from "../firebase";
import Compressor from "compressorjs";
import {
  getEventBaseStoragePath,
  getProductBaseStoragePath,
  isEventExternalLink,
  isFirebaseStorageUrl,
  isProductSpecificType,
} from "@markit/common.utils";
import { ListResult } from "firebase/storage";

export type MediaUpload = { blobUrl: string; uploadCode: string };

export enum MEDIA_UPLOAD_CODES {
  SUCCESS = "Success",
  TOO_BIG = "Too Big",
  INVALID = "Invalid",
  PERMISSIONS = "Permissions",
}

export const MAX_IMAGE_NO_COMPRESSION_SIZE = 10000000; // 10MB for no compression images
export const MAX_IMAGE_MEDIA_SIZE_TEXT = 4000000; // 4MB for images used within texts (profile pic, text media)
export const MIN_IMAGE_MEDIA_SIZE_TO_COMPRESS = 1000000;
export const MAX_COMPRESSED_IMAGE_SIZE_TEXT = 800000; // 800KB post-compression limit for text images
export const COMPRESSION_QUALITY_HIGH = 0.9;
export const COMPRESSION_QUALITY_LOW = 0.8;

export const errorAlertText = (code: string, compression?: boolean) => {
  switch (code) {
    case MEDIA_UPLOAD_CODES.TOO_BIG:
      return `Image is too big (max. ${
        (compression
          ? MAX_IMAGE_MEDIA_SIZE_TEXT
          : MAX_IMAGE_NO_COMPRESSION_SIZE) / 1000000
      }MB)`;
    case MEDIA_UPLOAD_CODES.INVALID:
      return "Invalid Media Type";
    case MEDIA_UPLOAD_CODES.PERMISSIONS:
      return "Sorry, please go into settings and enable camera permissions";
    default:
      return "";
  }
};

export const errorSubAlertText = (code: string) => {
  switch (code) {
    case MEDIA_UPLOAD_CODES.TOO_BIG:
      return "Try again with a smaller image";
    case MEDIA_UPLOAD_CODES.INVALID:
      return "Try again with a valid media type.\nValid media types include: jpeg, jpg, gif, png";
    case MEDIA_UPLOAD_CODES.PERMISSIONS:
      return "";
    default:
      return "";
  }
};

// Uploads a image media file to firebase storage
export const uploadMedia = async (
  input: React.ChangeEvent<HTMLInputElement> | File | FileList,
  square: boolean, // used only for uploading profile pic
  compress?: boolean // compress the image (ie. profile pics and text media)
): Promise<MediaUpload> => {
  try {
    const myFile =
      input instanceof File
        ? input
        : input instanceof FileList
          ? input[0]
          : input.target.files?.[0];

    if (myFile) {
      if (
        myFile.size >
        (compress ? MAX_IMAGE_MEDIA_SIZE_TEXT : MAX_IMAGE_NO_COMPRESSION_SIZE)
      ) {
        return {
          blobUrl: "",
          uploadCode: MEDIA_UPLOAD_CODES.TOO_BIG,
        };
      }
      const convertData: MediaUpload = compress
        ? await convertImage(myFile, square)
        : skipCompressionImage(myFile);
      if (convertData.uploadCode === MEDIA_UPLOAD_CODES.SUCCESS) {
        const response = await fetch(convertData.blobUrl);
        const blob = await response.blob();
        if (
          blob.size >
          (compress
            ? MAX_COMPRESSED_IMAGE_SIZE_TEXT
            : MAX_IMAGE_NO_COMPRESSION_SIZE)
        ) {
          return {
            blobUrl: "",
            uploadCode: MEDIA_UPLOAD_CODES.TOO_BIG,
          };
        }

        return {
          blobUrl: convertData.blobUrl,
          uploadCode: MEDIA_UPLOAD_CODES.SUCCESS,
        };
      } else {
        return convertData;
      }
    } else {
      return {
        blobUrl: "",
        uploadCode: MEDIA_UPLOAD_CODES.INVALID,
      };
    }
  } catch (e: any) {
    return {
      blobUrl: "",
      uploadCode: MEDIA_UPLOAD_CODES.INVALID,
    };
  }
};

// Helper function to handle image compression using Compressor.js
const compressImageWithCompressor = (
  blob: Blob,
  compressionQuality: number,
  size?: number
): Promise<MediaUpload> => {
  return new Promise((resolve, reject) => {
    const compressor = new Compressor(blob, {
      quality: compressionQuality,
      maxWidth: size || 800,
      maxHeight: size || 800,
      mimeType: "image/jpeg",
      success: (compressedBlob) => {
        // Double check the size and compress further if needed
        if (compressedBlob.size > MAX_COMPRESSED_IMAGE_SIZE_TEXT) {
          const emergencyCompressor = new Compressor(compressedBlob, {
            quality: 0.5,
            maxWidth: 600,
            maxHeight: 600,
            mimeType: "image/jpeg",
            success: (finalBlob) => {
              const blobURL = URL.createObjectURL(finalBlob);
              resolve({
                blobUrl: blobURL,
                uploadCode: MEDIA_UPLOAD_CODES.SUCCESS,
              });
            },
            error: (error) => {
              console.error("Emergency compression error:", error);
              reject(error);
            },
          });
        } else {
          const blobURL = URL.createObjectURL(compressedBlob);
          resolve({
            blobUrl: blobURL,
            uploadCode: MEDIA_UPLOAD_CODES.SUCCESS,
          });
        }
      },
      error: (error) => {
        console.error("Compression error:", error);
        reject(error);
      },
    });
  });
};

export const compressAndManipulateImage = async (
  url: string,
  compressionQuality: number,
  square: boolean,
  size?: number
): Promise<MediaUpload> => {
  return new Promise<MediaUpload>((resolve, reject) => {
    try {
      const img = new Image();
      img.src = url;

      img.onload = () => {
        // First, handle square cropping if needed
        if (square) {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          if (!ctx) {
            reject(new Error("Canvas context not supported"));
            return;
          }

          const size = Math.min(img.width, img.height);
          canvas.width = size;
          canvas.height = size;

          // Center the image
          const offsetX = (img.width - size) / 2;
          const offsetY = (img.height - size) / 2;

          // Resize canvas and redraw image based on new dimensions
          ctx.drawImage(img, -offsetX, -offsetY, img.width, img.height);

          // Convert to blob for compression
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error("Failed to create blob"));
              return;
            }

            // Use the modular compression function
            compressImageWithCompressor(blob, compressionQuality, size)
              .then(resolve)
              .catch(reject);
          }, "image/jpeg");
        } else {
          // For non-square images, compress directly
          fetch(url)
            .then((response) => response.blob())
            .then((blob) =>
              compressImageWithCompressor(blob, compressionQuality, size)
            )
            .then(resolve)
            .catch(reject);
        }
      };

      img.onerror = (error) => {
        console.error("Image loading error:", error);
        reject(error);
      };
    } catch (error) {
      console.error("Compression error:", error);
      reject(error);
    }
  });
};

// Skips the compression step for images and returns the blob url
export const skipCompressionImage = (myFile: File): MediaUpload => {
  return myFile.type.startsWith("image/")
    ? {
        blobUrl: URL.createObjectURL(myFile),
        uploadCode: MEDIA_UPLOAD_CODES.SUCCESS,
      }
    : {
        blobUrl: "",
        uploadCode: MEDIA_UPLOAD_CODES.INVALID,
      };
};

// Utilises HTML5's canvas properties to redraw and thus compress image
export const convertImage = (
  myFile: File,
  square: boolean,
  size?: number
): Promise<MediaUpload> => {
  return new Promise<MediaUpload>((resolve, reject) => {
    // Validate file type
    if (!myFile.type.startsWith("image/")) {
      reject(new Error("Invalid file type. Please upload an image."));
      return;
    }

    const fileReader = new FileReader();
    const compressionQuality =
      myFile.size > MIN_IMAGE_MEDIA_SIZE_TO_COMPRESS
        ? COMPRESSION_QUALITY_LOW
        : COMPRESSION_QUALITY_HIGH;

    fileReader.onload = async () => {
      try {
        const compressData = await compressAndManipulateImage(
          fileReader.result as string,
          compressionQuality,
          square,
          size
        );
        resolve(compressData);
      } catch (error) {
        console.error("Error compressing image:", error);
        reject(error);
      }
    };

    fileReader.onerror = (error) => {
      console.error("Error reading file:", error);
      reject(error);
    };

    if (myFile) {
      fileReader.readAsDataURL(myFile);
    } else {
      reject(new Error("No file provided"));
    }
  });
};

// TODO (jonathan): Peter set up this complicated logic, so I can't apply the saveMediaToFirestore helper function here like we do with all other places we save media
// Handles creating and storing the photo into firebase storage by creating the blob and using uploadBytes
// For external events, uses backend proxy to handle storing a third-party external photo url into firebase storage
// Due to CORS, we have to use our backend as a proxy to bypass the security rules
export const storeEventImageIntoFirebaseStorage = async (
  event: Event,
  url: string
): Promise<string> => {
  try {
    let blob: Blob = new Blob();
    // Investigate: For the editing event case, if image does not change, I had to make it enter the proxy case. Not sure why we have to use proxy now when we didn't before, it was fine without it
    // Use proxy if event type is an external event OR if Markit Event with external link
    if (
      isFirebaseStorageUrl(url) ||
      isEventExternalLink(event.eventType) ||
      (event.eventType === EventType.MARKIT && event.externalLink)
    ) {
      const proxyUrl = `${API_BASE}eventScraper/fetchExternalEventPhotoProxy?url=${encodeURIComponent(
        url
      )}`;
      const response = await fetch(proxyUrl);
      if (!response.ok) {
        // If the proxy does not work, try to fetch the photoUrl by itself.
        // Can hit this case if type is Markit and has externalLink, but image wasn't from third party
        const altResponse = await fetch(url);
        if (!altResponse.ok) {
          throw new Error("Network response was not ok");
        }
        const altBlob = await altResponse.blob();
        blob = altBlob;
      } else {
        blob = await response.blob();
      }

      // compress and convert image to a square
      // has to be done after the proxy stuff
      const uploadData = await compressAndManipulateImage(
        URL.createObjectURL(blob),
        0.7,
        !isEventExternalLink(event.eventType) // only compress to square if not an external link
      );

      if (uploadData.uploadCode !== MEDIA_UPLOAD_CODES.SUCCESS) {
        alert("Error converting image from external link");
      } else {
        blob = await fetch(uploadData.blobUrl).then((r) => r.blob());
      }
    } else {
      // Normal uploaded images from user
      blob = await fetch(url).then((r) => r.blob());
    }
    // Store in the correct storage path depending on the event type
    // For products, the image stored here is the social preview image
    const storagePath = isProductSpecificType(event.eventType)
      ? getProductBaseStoragePath(event.createdBy, event.id) + "/imagePreview"
      : getEventBaseStoragePath(event.createdBy, event.id) + `/${generate()}`;
    const imageRef = ref(firebaseStorage, storagePath);
    const metadata = {
      contentType: "image/jpeg",
    };
    return new Promise<string>((resolve, reject) => {
      // TODO (jonathan): we probably don't need this anymore since I added compressAndManipulateImage to apply to the original external link
      // This is just reducing the size of the image by a quality factor if it's a massive scraped image. Eventbrite and luma can only upload a max of ~10MB images
      // Just leaving for now since no reason to change it and can figure out if we can remove later
      (async () => {
        if (blob.size > MAX_COMPRESSED_IMAGE_SIZE_TEXT) {
          // eslint-disable-next-line no-new
          new Compressor(blob, {
            quality: 0.8,
            success: async (compressedResult) => {
              await uploadBytes(imageRef, compressedResult, metadata);
              const url = await getDownloadURL(imageRef);
              resolve(url);
            },
            error: (error) => {
              reject(error);
            },
          });
        } else {
          uploadBytes(imageRef, blob, metadata)
            .then(async () => {
              const url = await getDownloadURL(imageRef);
              resolve(url);
            })
            .catch((error) => {
              reject(error);
            });
        }
      })().catch(reject); // Handle any async errors
    });
  } catch (err: any) {
    console.log(err.message);
  }
  return "";
};

// Saves an image to a specified pathString in firebase storage
export const saveMediaToFirestore = async (
  url: string,
  pathString: string,
  contactCardUrl: string,
  fileName?: string
): Promise<string> => {
  try {
    // Check if the url is the same as the contact card url, and if so, return the url
    if (contactCardUrl && url === contactCardUrl) {
      return url;
    }

    // Fetch the blob from the URL
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `Failed to fetch image: ${response.status} ${response.statusText}`
      );
    }

    const blob = await response.blob();
    const imageRef = ref(firebaseStorage, pathString);
    const contentType = blob.type || "application/octet-stream";
    await uploadBytes(imageRef, blob, {
      contentType,
      customMetadata: fileName ? { fileName } : {},
    });
    const downloadUrl = await getDownloadURL(imageRef);

    // Double-check that we're not returning a blob URL
    if (downloadUrl.startsWith("blob:")) {
      throw new Error(
        "Firebase returned a blob URL instead of a proper download URL"
      );
    }

    return downloadUrl;
  } catch (error: any) {
    console.error("Error in saveMediaToFirestore:", error.message);
    throw error;
  }
};

// Deletes a single file or all files in a folder from Firebase Storage
// If the path is a single file, it deletes that file
// If the path is a folder (prefix), it deletes all files in that folder
export const deleteMediaFromFirestore = async (path: string) => {
  try {
    const fileRef = ref(firebaseStorage, path);

    // First, try to delete it as a single file
    try {
      await deleteObject(fileRef);
    } catch (fileError: any) {
      // If it's not a file (or doesn't exist), try as a folder
      const listResult = await listAll(fileRef);
      const deletePromises = listResult.items.map((itemRef) =>
        deleteObject(itemRef)
      );
      await Promise.all(deletePromises);
    }
  } catch (error: any) {
    // If folder doesn't exist or is already empty, that's okay
    if (error.code === "storage/object-not-found") {
      return;
    }
    console.error("Error in deleteMediaFromFirestore:", error.message);
  }
};

// Visually remove the specified smaple photos from the selectable images
export const filteredSampleImages = async (listResult: ListResult) => {
  const defaultImages = await Promise.all(
    listResult.items.map(async (item) => {
      const url = await getDownloadURL(item);
      return url;
    })
  );
  const filteredImages = defaultImages.filter(
    (image) =>
      !image.includes("auraOne.jpeg") && !image.includes("willSmithMarkit.jpg")
  );
  return filteredImages;
};

export const getImageAspectRatio = async (
  imageUrl: string
): Promise<MediaAspectRatio | null> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const width = img.width;
        const height = img.height;

        if (!width || !height) {
          resolve(null);
          return;
        }

        const aspectRatio = width / height;

        // Define the aspect ratios and their corresponding enum values
        const ratios = [
          { value: 16 / 9, enum: MediaAspectRatio._16_9 },
          { value: 4 / 3, enum: MediaAspectRatio._4_3 },
          { value: 9 / 16, enum: MediaAspectRatio._9_16 },
          { value: 3 / 4, enum: MediaAspectRatio._3_4 },
          { value: 1 / 1, enum: MediaAspectRatio._1_1 },
        ];

        // Find the closest matching aspect ratio
        let closestRatio = ratios[0];
        let minDifference = Math.abs(aspectRatio - ratios[0].value);

        for (const ratio of ratios) {
          const difference = Math.abs(aspectRatio - ratio.value);
          if (difference < minDifference) {
            minDifference = difference;
            closestRatio = ratio;
          }
        }

        resolve(closestRatio.enum);
      } catch (error) {
        console.error("Error calculating aspect ratio:", error);
        resolve(null);
      }
    };

    img.onerror = (error) => {
      console.error("Error loading image for aspect ratio detection:", error);
      resolve(null);
    };

    img.src = imageUrl;
  });
};
